import uuid from 'uuid';

import * as loggerx from 'utilities/loggerx';

class SegmentHandler {
  queue = [];
  segments = [];
  idsMap = new Map();
  currentSegment = {};
  previousSegment = {};

  playbackRate = 1;
  prevPlaybackRate = 1;

  processing = false;
  finalizing = false;
  sendFn;

  registerServerHandler(fn) {
    this.sendFn = fn;
  }

  /**
   * Add current segment increment to the queue.
   * If no ID has been prvided, this is the start of a new segment,
   * update this.currentSegment.
   * @param {object} segment - a play segment increment object { start, end, id? }
   */
  _add(segment) {
    if (!segment.cid) {
      this.currentSegment.cid = uuid.v4();
      segment.cid = this.currentSegment.cid;
    }

    this.queue.push(segment);
    this._process().catch(console.error);
  }

  /**
   * Finalizes segments by closing the last one on page close
   * Returns a summary of segments in case something was not
   * saved properly.
   * @param {number} time - current video time
   */
  finalize(time) {
    this.finalizing = true;
    this.close(time);
    loggerx.debug('finalizing', { time, queue: this.queue });
    this.queue.forEach(segment => {
      const currentSegment = this.segments.find(s => s.cid === segment.cid);

      if (currentSegment) {
        currentSegment.end = segment.end;
      } else {
        this.segments.push(segment);
      }
    });

    return this.segments;
  }

  async _process() {
    if (this.finalizing) {
      return;
    }

    const segment = this.queue.shift();

    try {
      if (!segment || this.processing) {
        return;
      }

      // keep a reference to current segment
      // being saved to avoid getting different ids
      // from the server for the same client id
      this.processing = true;

      await this._attemptServerUpdate(segment);
      await this._attemptServerCreate(segment);

      this.processing = false;
      // console.table(this.segments);
      // console.table(this.intervalBuckets);

      if (this.queue.length) {
        await this._process();
      }
    } catch (e) {
      this.processing = false;

      if (!segment) {
        return;
      }

      // put back unprocessed segment in the queue
      this.queue.unshift(segment);
    }
  }

  /**
   * Attempt server update.
   * If the segment increment client id is mapped to a server id
   * send a server update and update the segments queue.
   * @param {object} segment - Play segment increment
   */
  async _attemptServerUpdate(segment) {
    if (!this.idsMap.has(segment.cid)) {
      return;
    }

    const sid = this.idsMap.get(segment.cid);

    await this._send({ ...segment, sid });
    const currentSegment = this.segments.pop();

    currentSegment.end = segment.end;
    this.segments.push(currentSegment);
    loggerx.debug('updated segment', { segment });
  }

  /**
   * Attempt server create.
   * Send new play segment to the server,
   * add new entry in the server <> client ids map,
   * push new segment to the queue.
   * @param {object} segment - Play segment increment
   */
  async _attemptServerCreate(segment) {
    if (this.idsMap.has(segment.cid)) {
      return;
    }

    const sid = await this._send(segment);

    this.idsMap.set(segment.cid, sid);
    this.segments.push({ ...segment, id: sid });
    loggerx.debug('created segment', { ...segment, id: sid });
  }

  /**
   * Send segment to the server and return back the newly created id
   * @param {object} segment - Play segment increment
   */
  async _send(segment) {
    const { start, end, speed, sid } = segment;
    const data = await this.sendFn({ start, end, speed, sid });

    if (!data) {
      return;
    }

    return data.id;
  }

  _getLastClosedSegment() {
    return this.segments[this.segments.length - 1];
  }

  /**
   * Start a new play segment.
   * Each increment belonging to this play segment
   * will build from here.
   * @param {number} time - video current time in seconds
   */
  create(time) {
    this.currentSegment = { start: time };

    // segment started from where last
    // segment was closed, update that one.
    if (this.previousSegment.end === time) {
      this.currentSegment.cid = this.previousSegment.cid;
    }
  }

  /**
   * Update and send current play segment increment.
   * If playback rate has changed since last tracked segment,
   * a new one is created.
   * If current segment has an ID, this will be used for the next increments,
   * otherwise a new one will be created.
   * @param {number} time - video current time in seconds
   */
  update(time) {
    const { cid, end, start } = this.currentSegment;

    if (start == null) {
      return;
    }

    const segmentStart = end != null ? end : start;
    const hasSpeedChanged = this.prevPlaybackRate !== this.playbackRate;
    const segment = {
      start: segmentStart,
      end: time,
      // segment icrement will be marked as watched
      // at the current playback rate even if
      // playback rate has just changed.
      speed: this.playbackRate,
    };

    this.prevPlaybackRate = this.playbackRate;

    // when using the UA player (as opposed to our UI)
    // using the rewind button might cause
    // issues: https://sentry.io/organizations/loom-ng/issues/2086439939
    // as the action is undetected by us
    if (end > 0 && time < end) {
      this.close(end);
      this.create(time);

      return;
    }

    if (hasSpeedChanged) {
      this.create(time);
      this._add(segment);

      return;
    }

    this.currentSegment.end = time;
    this._add({ ...segment, cid });
  }

  /**
   * Send current play segment increment and start a new series.
   * @param {number} time - video current time in seconds
   */
  close(time) {
    this.update(time);
    this.previousSegment = { ...this.currentSegment };
    this.currentSegment = {};
  }

  /**
   * Change the current tracked playback rate.
   * We assume a segment has been played at the currently track
   * playback rate. This is not the most accurate way, but
   * - the margin of error is small (5s)
   * - it makes the code much simpler
   * @param {number} playbackRate - video current playback rate
   */
  speed(playbackRate) {
    this.playbackRate = playbackRate;
  }
}

export default SegmentHandler;
